<template>
  <PageWrap
    :heading="$t('admin.nav.links.export')"
    :loading="$apollo.loading"
  >
    <Flex direction="column">
      <BaseCard :tabs="tabs">
        <Section
          v-if="activeTab === 'monthly'"
          :months="months"
          :loading="$apollo.loading"
          v-bind="{ dataType, fileFormat }"
        />
        <Section
          v-else-if="activeTab === 'lifetime'"
          v-bind="{ dataType, fileFormat }"
        />
        <Fiskaly v-else-if="activeTab === 'fiskaly'" />
      </BaseCard>
      <RemoteExportList />
    </Flex>
  </PageWrap>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import PageWrap from '@/modules/admin/PageWrap.vue';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import Fiskaly from './Fiskaly.vue';
import RemoteExportList from './RemoteExportList.vue';
import Section from './Section.vue';

export default defineComponent({
  components: {
    Section,
    RemoteExportList,
    PageWrap,
    Fiskaly,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const period = computed(() => route.query.period || 'monthly');
    const dataType = computed(() => route.query.dataType as string);
    const fileFormat = computed(() => route.query.fileFormat as string);
    const { hasFeatureFlag } = useUserStore();
    const hasFiskaly = hasFeatureFlag('fiskaly-sign-enabled');

    const tabs = ref(
      [
        {
          label: t('export_page.heading_monthly'),
          value: 'monthly',
          active: period.value === 'monthly',
          id: 'monthly',
        },
        {
          label: t('export_page.heading_lifetime'),
          value: 'lifetime',
          active: period.value === 'lifetime',
          id: 'lifetime',
        },
        hasFiskaly
          ? {
              label: t('export_page.fiskaly_archive.heading'),
              value: 'fiskaly',
              active: period.value === 'fiskaly',
              id: 'fiskaly',
            }
          : null,
      ].filter(Boolean),
    );
    return {
      tabs,
      dataType,
      fileFormat,
    };
  },
  computed: {
    ...mapState(useCompanyStore, ['company']),
    hasRemoteAsyncExports() {
      return unleash.isEnabled('NewAsyncExports');
    },
    activeTab() {
      return (this.tabs.find((tab) => tab.active) || this.tabs[0]).value;
    },
  },
  apollo: {
    months: {
      query: gql`
        query getExports {
          exports {
            months
          }
        }
      `,
      update(data) {
        return data.exports.months;
      },
    },
  },
});
</script>
